import React, { useContext, useEffect, useState } from "react";
import { CommonContext } from "hooks/CommonContext";
import RightSidebar from "./RightSidebar";
import Drawer from "components/drawer";
import HumanMeetingRoom from "pages/human-meeting-room";
import LogoLabel from "components/ui-components/LogoLabel";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  html, body {
    margin: 0; padding: 0;
    overflow: hidden !important;
    height: 100% !important;
    background: #080d18 !important;
  }

  .root {
    all: unset !important;
    display: flex !important;
    flex-direction: column !important;
    height: 100vh !important;
    max-height: 100vh !important;
    width: 100vw !important;
    overflow: hidden !important;
    background: #080d18 !important;
    font-family: 'Inter', system-ui, sans-serif !important;
    box-sizing: border-box !important;
  }

  .main-meeting-container {
    all: unset !important;
    display: flex !important;
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  .rl-header {
    height: 48px;
    min-height: 48px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    background: #0b1120;
    border-bottom: 1px solid #1a2540;
    z-index: 50;
    box-sizing: border-box;
  }

  .rl-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    overflow: hidden;
  }

  .rl-logo {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
  .rl-logo img { height: 22px !important; }

  .rl-header-divider {
    width: 1px;
    height: 20px;
    background: #1a2540;
    flex-shrink: 0;
  }

  .rl-live {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #22c55e;
    flex-shrink: 0;
    box-shadow: 0 0 0 0 rgba(34,197,94,0.4);
    animation: rlPing 2.2s ease-in-out infinite;
  }

  @keyframes rlPing {
    0%   { box-shadow: 0 0 0 0   rgba(34,197,94,0.5); }
    70%  { box-shadow: 0 0 0 7px rgba(34,197,94,0); }
    100% { box-shadow: 0 0 0 0   rgba(34,197,94,0); }
  }

  /* Job title — bright white, clearly readable */
  .rl-job {
    font-size: 13.5px;
    font-weight: 600;
    color: #e8eeff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 50vw;
  }

  .rl-sep {
    color: #4a5f82;
    flex-shrink: 0;
    font-size: 16px;
    line-height: 1;
  }

  /* Candidate name — softer but still readable */
  .rl-candidate {
    font-size: 14px;
    font-weight: 400;
    color: #8b9dc3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  /* Clock — muted but legible */
  .rl-time {
    font-size: 13px;
    font-weight: 500;
    color: #6b7fa8;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.06em;
    flex-shrink: 0;
  }

  .rl-room-area {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

const RoomLayout = ({ children }) => {
  inject();
  const { interviewDetails, leaveInterview } = useContext(CommonContext);
  const [time, setTime] = useState(getTime());

  useEffect(() => {
    window.addEventListener("beforeunload", leaveInterview);
    return () => window.removeEventListener("beforeunload", leaveInterview);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTime(getTime()), 5000);
    return () => clearInterval(t);
  }, []);

  function getTime() {
    return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  const job = interviewDetails?.jobDetails?.displayName ?? "";
  const candidate = [
    interviewDetails?.candidateDetails?.firstName,
    interviewDetails?.candidateDetails?.lastName,
  ].filter(Boolean).join(" ");

  return (
    <div className="root">
      <div className="rl-header">
        <div className="rl-header-left">
          <span className="rl-logo">
            <LogoLabel />
          </span>
          <span className="rl-header-divider" />
          <span className="rl-live" />
          <span className="rl-job">{job}</span>
          {candidate && (
            <>
              <span className="rl-sep">·</span>
              <span className="rl-candidate">{candidate}</span>
            </>
          )}
        </div>
        <span className="rl-time">{time}</span>
      </div>

      <div className="main-meeting-container">
        <div className="rl-room-area">
          <HumanMeetingRoom />
        </div>
        <Drawer />
        <RightSidebar />
      </div>

      {children}
    </div>
  );
};

export default RoomLayout;